// @ts-nocheck
/**
 * 📱 MOBILE VIEWPORT OPTIMIZATION
 * Optimizes viewport settings for mobile devices
 * Handles safe areas, orientation changes, and keyboard adjustments
 */

interface ViewportOptions {
  preventZoom?: boolean;
  useSafeAreas?: boolean;
  fixKeyboardIssues?: boolean;
  enablePWAMode?: boolean;
  statusBarStyle?: 'default' | 'black' | 'black-translucent';
  themeColor?: string;
}

/**
 * Set optimal mobile viewport settings
 */
export const setOptimalMobileViewport = (options: ViewportOptions = {}): void => {
  const {
    preventZoom = true,
    useSafeAreas = true,
    fixKeyboardIssues = true,
    enablePWAMode = true,
    statusBarStyle = 'black-translucent',
    themeColor = '#e91e63'
  } = options;

  // Get existing viewport meta tag or create a new one
  let viewport = document.querySelector('meta[name="viewport"]');
  if (!viewport) {
    viewport = document.createElement('meta');
    viewport.name = 'viewport';
    document.head.appendChild(viewport);
  }

  // Set optimal viewport content
  let viewportContent = 'width=device-width, initial-scale=1.0';
  
  // Prevent pinch zoom if specified
  if (preventZoom) {
    viewportContent += ', maximum-scale=1.0, user-scalable=no';
  }
  
  // Add viewport-fit=cover for notched devices if using safe areas
  if (useSafeAreas) {
    viewportContent += ', viewport-fit=cover';
  }
  
  viewport.setAttribute('content', viewportContent);

  // Add safe area CSS if enabled (V334-LAYOUT-01: inject once to prevent accumulation on HMR)
  if (useSafeAreas && !document.querySelector('style[data-safe-area-injected]')) {
    const safeAreaStyle = document.createElement('style');
    safeAreaStyle.setAttribute('data-safe-area-injected', 'true');
    safeAreaStyle.textContent = `
      .safe-area-top {
        padding-top: env(safe-area-inset-top);
      }
      .safe-area-bottom {
        padding-bottom: env(safe-area-inset-bottom);
      }
      .safe-area-left {
        padding-left: env(safe-area-inset-left);
      }
      .safe-area-right {
        padding-right: env(safe-area-inset-right);
      }
      .safe-area {
        padding: env(safe-area-inset-top) env(safe-area-inset-right) env(safe-area-inset-bottom) env(safe-area-inset-left);
      }
      .min-safe-h-screen {
        min-height: calc(100vh + env(safe-area-inset-top) + env(safe-area-inset-bottom));
      }
    `;
    document.head.appendChild(safeAreaStyle);
  }

  // Fix iOS keyboard issues if enabled
  // V291-FIX: Gate keyboard CSS and listeners to iOS Safari ONLY (not Capacitor, not Android)
  // The body.keyboard-open height freeze is harmful for Android native and chat
  if (fixKeyboardIssues) {
    // Only inject keyboard CSS for iOS Safari (not Capacitor native, not Android)
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const isIOSSafari = isIOS && !window.Capacitor?.isNativePlatform();
    
    if (isIOSSafari) {
      const keyboardStyle = document.createElement('style');
      keyboardStyle.textContent = `
        input, textarea, select {
          font-size: 16px !important;
        }
        
        body.keyboard-open {
          height: calc(100% + 1px);
          overflow: hidden;
        }
      `;
      document.head.appendChild(keyboardStyle);

      // Only add body.keyboard-open listeners for iOS Safari
      window.addEventListener('focusin', (e) => {
        if (['INPUT', 'TEXTAREA', 'SELECT'].includes((e.target as HTMLElement).tagName)) {
          document.body.classList.add('keyboard-open');
          // Scroll to input after a short delay
          setTimeout(() => {
            (e.target as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'center' });
          }, 300);
        }
      });
      
      window.addEventListener('focusout', () => {
        document.body.classList.remove('keyboard-open');
        // Restore scroll position
        window.scrollTo({ top: 0, behavior: 'smooth' });
      });
    }
  }

  // Enable PWA mode if specified
  if (enablePWAMode) {
    // Add mobile web app capable meta tags
    const addMetaTag = (name: string, content: string) => {
      let meta = document.querySelector(`meta[name="${name}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute('name', name);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };

    addMetaTag('mobile-web-app-capable', 'yes');
    addMetaTag('apple-mobile-web-app-capable', 'yes');
    addMetaTag('apple-mobile-web-app-status-bar-style', statusBarStyle);
    addMetaTag('theme-color', themeColor);
    
    // Add orientation lock for portrait mode
    addMetaTag('screen-orientation', 'portrait');
  }
};

/**
 * Handle device orientation changes
 */
export const handleOrientationChanges = (
  onPortrait?: () => void,
  onLandscape?: () => void
): () => void => {
  const checkOrientation = () => {
    const isPortrait = window.innerHeight > window.innerWidth;
    if (isPortrait && onPortrait) {
      onPortrait();
    } else if (!isPortrait && onLandscape) {
      onLandscape();
    }
  };

  // Check initial orientation
  checkOrientation();

  // Listen for orientation changes
  window.addEventListener('resize', checkOrientation);
  window.addEventListener('orientationchange', checkOrientation);

  // Return cleanup function
  return () => {
    window.removeEventListener('resize', checkOrientation);
    window.removeEventListener('orientationchange', checkOrientation);
  };
};

/**
 * Get device information
 */
export const getDeviceInfo = () => {
  const ua = navigator.userAgent;
  const isIOS = /iPad|iPhone|iPod/.test(ua);
  const isAndroid = /Android/.test(ua);
  const isMobile = isIOS || isAndroid || window.innerWidth < 768;
  const isTablet = (isIOS || isAndroid) && window.innerWidth >= 768 && window.innerWidth < 1024;
  const isDesktop = !isMobile && !isTablet;
  const hasNotch = isIOS && /iPhone X|iPhone 11|iPhone 12|iPhone 13|iPhone 14|iPhone 15/.test(ua);
  
  return {
    isIOS,
    isAndroid,
    isMobile,
    isTablet,
    isDesktop,
    hasNotch,
    devicePixelRatio: window.devicePixelRatio || 1,
    screenWidth: window.screen.width,
    screenHeight: window.screen.height,
    viewportWidth: window.innerWidth,
    viewportHeight: window.innerHeight
  };
};

/**
 * Fix 100vh issue on mobile browsers
 */
export const fixMobileVhIssue = (): void => {
  const setVhVariable = () => {
    // Set CSS variable with the actual viewport height
    const vh = window.innerHeight * 0.01;
    document.documentElement.style.setProperty('--vh', `${vh}px`);
  };

  // Set the variable initially
  setVhVariable();

  // Update on resize and orientation change
  window.addEventListener('resize', setVhVariable);
  window.addEventListener('orientationchange', setVhVariable);

  // Add CSS to use the variable
  const style = document.createElement('style');
  style.textContent = `
    .h-screen-real {
      height: calc(var(--vh, 1vh) * 100);
    }
    .min-h-screen-real {
      min-height: calc(var(--vh, 1vh) * 100);
    }
    .max-h-screen-real {
      max-height: calc(var(--vh, 1vh) * 100);
    }
  `;
  document.head.appendChild(style);
};

/**
 * Initialize all mobile viewport optimizations
 */
export const initializeMobileViewport = (options: ViewportOptions = {}): void => {
  setOptimalMobileViewport(options);
  fixMobileVhIssue();
};

export default {
  setOptimalMobileViewport,
  handleOrientationChanges,
  getDeviceInfo,
  fixMobileVhIssue,
  initializeMobileViewport
};

